import { Component, inject, PLATFORM_ID, signal, computed, OnInit } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AdminService } from '../../services/admin/admin.service';
import { ApiKey, ApiKeyQueryParams, ApiKeysResponse } from '../../interfaces/responses/api-keys.interface';
import { PaginationComponent } from '../../components/pagination/pagination.component';
import { ProgressLoaderComponent } from '../../components/loaders/progress-loader/progress-loader.component';
import { ToastService } from '../../services/general/toast.service';
import { CustomButtonComponent } from '../../components/buttons/custom-button/custom-button.component';
import { SearchComponent } from '../../shared/search/search.component';
import { ImagesService } from '../../services/general/images.service';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { GenerateApiKeyComponent } from '../../components/overlays/generate-api-key/generate-api-key.component';
import { PermissionService } from '../../services/permission/permission.service';
import { PrimaryButtonComponent } from '../../components/buttons/primary-button/primary-button.component';
import { ConfirmationDialogComponent } from '../../components/shared/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-api-keys',
  standalone: true,
  imports: [
    CommonModule,
    PaginationComponent,
    ProgressLoaderComponent,
    ReactiveFormsModule,
    FormsModule,
    GenerateApiKeyComponent,
    PrimaryButtonComponent,
    ConfirmationDialogComponent
  ],
  templateUrl: './api-keys.component.html',
  styleUrl: './api-keys.component.scss'
})
export class ApiKeysComponent implements OnInit {
  private readonly adminService = inject(AdminService);
  protected readonly permissionService = inject(PermissionService);
  private readonly _toast = inject(ToastService);
  private readonly platformId = inject(PLATFORM_ID);
  protected readonly _images = inject(ImagesService);
  private readonly _fb = inject(FormBuilder);

  // Signals for state management
  readonly isLoading = signal(false);
  viewMode: 'table' | 'grid' = 'table';
  readonly apiKeys = signal<ApiKey[]>([]);
  readonly apiKeysResponse = signal<ApiKeysResponse | undefined>(undefined);
  readonly isGenerateApiKeyModal = signal(false);
  readonly isEditOriginsModal = signal(false);
  readonly editingApiKey = signal<ApiKey | null>(null);
  editOriginEntries: string[] = [];
  editOriginDraft = '';
  editOriginsError = '';
  isUpdatingOrigins = false;
  readonly revealedSecretIds = signal<Set<string>>(new Set());
  showClearRestrictionsConfirm = false;

  readonly queryParams = signal<ApiKeyQueryParams>({
    page: 1,
    perPage: 10,
    paginate: true,
    keyword: '',
  });

  readonly searchQuery = this._fb.group({
    keyword: [''],
  });

  // Computed values
  readonly hasApiKeys = computed(() => this.apiKeys().length > 0);
  readonly totalItems = computed(() => this.apiKeysResponse()?.meta?.total || 0);
  readonly totalPages = computed(() => this.apiKeysResponse()?.meta?.totalPages || 0);
  readonly currentPage = computed(() => this.apiKeysResponse()?.meta?.page || 1);
  readonly perPage = computed(() => this.apiKeysResponse()?.meta?.perPage || 10);

  ngOnInit(): void {
    this.loadApiKeys();

    if (isPlatformBrowser(this.platformId)) {
      this.searchQuery.get('keyword')?.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged()
        )
        .subscribe((query: string | null) => {
          const keyword = (query || '').trim();
          this.queryParams.update(params => ({
            ...params,
            keyword,
            page: 1
          }));
          this.loadApiKeys();
        });
    }
  }

  loadApiKeys(): void {
    this.isLoading.set(true);
    this.adminService.getApiKeys(this.queryParams()).subscribe({
      next: (res) => {
        this.apiKeysResponse.set(res);
        this.apiKeys.set(res.data ?? []);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error fetching API keys:', err);
        this._toast.error('Error loading API keys');
        this.isLoading.set(false);
      }
    });
  }

  onPageChange(page: number): void {
    this.queryParams.update(params => ({ ...params, page }));
    this.loadApiKeys();
  }

  isSecretRevealed(id: string): boolean {
    return this.revealedSecretIds().has(id);
  }

  toggleSecretReveal(id: string): void {
    this.revealedSecretIds.update(ids => {
      const next = new Set(ids);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  maskSecret(secret: string): string {
    return '••••••••••••••••••••';
  }

  copyToClipboard(text: string, type: 'public' | 'secret'): void {
    if (isPlatformBrowser(this.platformId)) {
      navigator.clipboard.writeText(text).then(() => {
        this._toast.success(`${type === 'public' ? 'Public' : 'Secret'} key copied to clipboard`);
      }).catch(() => {
        this._toast.error('Failed to copy to clipboard');
      });
    }
  }

  addApiKeyClicked(): void {
    this.isGenerateApiKeyModal.set(true);
  }

  onCloseGenerateApiKeyModal(): void {
    this.isGenerateApiKeyModal.set(false);
  }

  onApiKeyGenerated(): void {
    this.loadApiKeys();
    this.isGenerateApiKeyModal.set(false);
  }

  openEditOrigins(apiKey: ApiKey): void {
    this.editingApiKey.set(apiKey);
    this.editOriginEntries = [...(apiKey.allowedOrigins ?? [])];
    this.editOriginDraft = '';
    this.editOriginsError = '';
    this.isEditOriginsModal.set(true);
  }

  closeEditOriginsModal(): void {
    this.isEditOriginsModal.set(false);
    this.editingApiKey.set(null);
    this.showClearRestrictionsConfirm = false;
  }

  // Splits a raw string into candidate entries across commas, spaces, and newlines.
  private tokenizeOrigins(raw: string): string[] {
    return (raw || '').split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
  }

  private isValidPort(port: string): boolean {
    if (!/^\d{1,5}$/.test(port)) return false;
    const n = Number(port);
    return n >= 1 && n <= 65535;
  }

  private isIPv4(value: string): boolean {
    if (!/^(\d{1,3}\.){3}\d{1,3}$/.test(value)) return false;
    return value.split('.').every(octet => Number(octet) <= 255);
  }

  // Pragmatic check only — the backend is the source of truth.
  private isIPv6(value: string): boolean {
    if (!value.includes(':')) return false;
    if (!/^[0-9a-f:]+$/i.test(value)) return false;
    if ((value.match(/::/g) || []).length > 1) return false;
    const groups = value.split(':').filter(Boolean);
    return groups.length <= 8 && groups.every(g => g.length <= 4);
  }

  private isValidHostname(value: string): boolean {
    return /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/i.test(value);
  }

  // Returns an error message, or null when the entry is a valid origin, host, IP, or CIDR.
  private validateOriginEntry(entry: string): string | null {
    if (entry.includes('://')) {
      let url: URL;
      try {
        url = new URL(entry);
      } catch {
        return `"${entry}" is not a valid origin`;
      }
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return `"${entry}" must use http or https`;
      }
      if (url.pathname !== '/' || url.search || url.hash) {
        return `"${entry}" — origins cannot include a path`;
      }
      return null;
    }

    if (entry.includes('/')) {
      const parts = entry.split('/');
      const [addr, prefix] = parts;
      if (parts.length !== 2 || !/^\d{1,3}$/.test(prefix)) {
        return `"${entry}" — origins cannot include a path`;
      }
      if (this.isIPv4(addr)) {
        return Number(prefix) <= 32 ? null : `"${entry}" is not a valid CIDR range`;
      }
      if (this.isIPv6(addr)) {
        return Number(prefix) <= 128 ? null : `"${entry}" is not a valid CIDR range`;
      }
      return `"${entry}" is not a valid CIDR range`;
    }

    const colonCount = (entry.match(/:/g) || []).length;
    if (colonCount >= 2) {
      return this.isIPv6(entry) ? null : `"${entry}" is not a valid IPv6 address`;
    }

    let host = entry;
    if (colonCount === 1) {
      const [h, port] = entry.split(':');
      if (!this.isValidPort(port)) return `"${entry}" has an invalid port`;
      host = h;
    }
    if (this.isIPv4(host) || this.isValidHostname(host)) return null;
    return `"${entry}" is not a valid origin, host, IP, or CIDR range`;
  }

  // Normalizes for client-side display/dedupe; the backend re-normalizes on save.
  private normalizeOriginEntry(entry: string): string {
    return entry.toLowerCase().replace(/\/+$/, '');
  }

  // Moves whatever is in the draft input into the chip list.
  // Returns false (and sets the inline error) if any token is invalid.
  commitOriginDraft(): boolean {
    const tokens = this.tokenizeOrigins(this.editOriginDraft);
    if (!tokens.length) {
      this.editOriginDraft = '';
      return true;
    }
    for (const token of tokens) {
      const error = this.validateOriginEntry(token);
      if (error) {
        this.editOriginsError = error;
        return false;
      }
    }
    const merged = [...this.editOriginEntries];
    for (const token of tokens) {
      const normalized = this.normalizeOriginEntry(token);
      if (!merged.some(e => this.normalizeOriginEntry(e) === normalized)) {
        merged.push(normalized);
      }
    }
    this.editOriginEntries = merged;
    this.editOriginDraft = '';
    this.editOriginsError = '';
    return true;
  }

  onOriginDraftKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.commitOriginDraft();
    } else if (event.key === 'Backspace' && !this.editOriginDraft && this.editOriginEntries.length) {
      this.editOriginEntries = this.editOriginEntries.slice(0, -1);
    }
  }

  onOriginDraftPaste(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text');
    if (!text) return;
    event.preventDefault();
    this.editOriginDraft = [this.editOriginDraft, text].filter(Boolean).join(' ');
    this.commitOriginDraft();
  }

  onOriginDraftChanged(): void {
    if (this.editOriginsError) this.editOriginsError = '';
  }

  removeOriginEntry(index: number): void {
    this.editOriginEntries = this.editOriginEntries.filter((_, i) => i !== index);
  }

  submitEditOrigins(): void {
    if (!this.commitOriginDraft()) return;
    this.updateOrigins(this.editOriginEntries);
  }

  requestClearRestrictions(): void {
    this.showClearRestrictionsConfirm = true;
  }

  confirmClearRestrictions(): void {
    this.showClearRestrictionsConfirm = false;
    this.editOriginEntries = [];
    this.editOriginDraft = '';
    this.editOriginsError = '';
    this.updateOrigins([]);
  }

  cancelClearRestrictions(): void {
    this.showClearRestrictionsConfirm = false;
  }

  private updateOrigins(allowedOrigins: string[]): void {
    const apiKey = this.editingApiKey();
    if (!apiKey || this.isUpdatingOrigins) return;
    this.isUpdatingOrigins = true;
    this.editOriginsError = '';
    this.adminService.updateApiKeyOrigins(apiKey.id, allowedOrigins).subscribe({
      next: (updated) => {
        this.isUpdatingOrigins = false;
        // Reflect the backend's normalized (lowercased, deduped) list, not the submitted input.
        const stored = updated.allowedOrigins ?? [];
        this.apiKeys.update(keys => keys.map(k =>
          k.id === apiKey.id ? { ...k, allowedOrigins: stored } : k
        ));
        this._toast.success(stored.length
          ? 'Allowed origins updated — changes take effect within about a minute'
          : 'All restrictions removed — this key now accepts requests from any origin or IP');
        this.closeEditOriginsModal();
      },
      error: (error) => {
        this.isUpdatingOrigins = false;
        if (error?.status === 400 && error?.error?.message) {
          this.editOriginsError = error.error.message;
        } else if (error?.status === 404) {
          this._toast.error('This API key no longer exists or has been revoked');
          this.closeEditOriginsModal();
          this.loadApiKeys();
        } else {
          console.error('Error updating allowed origins:', error);
          this._toast.error('Error updating allowed origins');
        }
      }
    });
  }

  accessSummary(apiKey: ApiKey): string {
    const origins = apiKey.allowedOrigins?.length ?? 0;
    if (origins === 0) {
      return 'Unrestricted';
    }
    return `${origins} origin${origins === 1 ? '' : 's'}`;
  }

  accessTooltip(apiKey: ApiKey): string {
    const origins = apiKey.allowedOrigins ?? [];
    if (origins.length === 0) {
      return 'No origin restrictions';
    }
    return origins.join('\n');
  }
}
